<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { Search, UserPlus } from 'lucide-vue-next'
import parsePhoneNumber from 'libphonenumber-js'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import { Input } from '@/components/ui/input'
import { MemberAvatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface SearchMember {
  member_id: number
  display_name: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  avatar_url: string | null
  verified?: boolean
  is_project_member?: boolean
  is_invited?: boolean
}

const props = defineProps<{
  orgId: number
  projectId?: number | string | null
  placeholder?: string
  hideAddNew?: boolean
}>()

const emit = defineEmits<{
  select: [member: SearchMember]
  addNew: [contact: string]
}>()

const EMAIL_REGEX = /^\S+@\S+\.\S+$/

interface ParsedContact {
  email: string | null
  phone: string | null
}

const parseContact = (value: string): ParsedContact | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (EMAIL_REGEX.test(trimmed)) return { email: trimmed, phone: null }
  try {
    const phoneNumber = parsePhoneNumber(trimmed, { defaultCountry: 'US' })
    if (phoneNumber?.isValid()) return { email: null, phone: phoneNumber.number as string }
  } catch {
    // fall through
  }
  return null
}

const store = useStore()
const searchText = ref('')
const results = ref<SearchMember[]>([])
const isOpen = ref(false)
const isLoading = ref(false)
const hasSearched = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | null = null

const doSearch = async (query: string) => {
  if (query.length < 2) {
    results.value = []
    hasSearched.value = false
    isOpen.value = false
    return
  }

  isLoading.value = true
  hasSearched.value = true

  try {
    const data = await store.dispatch('org/searchMembers', {
      orgId: props.orgId,
      query,
      projectId: props.projectId || undefined,
    })
    results.value = data || []
    isOpen.value = true
  } catch {
    results.value = []
  } finally {
    isLoading.value = false
  }
}

watch(searchText, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => doSearch(val), 300)
})

const isDisabled = (member: SearchMember) => member.is_project_member || member.is_invited

const selectMember = (member: SearchMember) => {
  if (isDisabled(member)) return
  emit('select', member)
  searchText.value = ''
  results.value = []
  isOpen.value = false
  hasSearched.value = false
}

const parsedContact = computed(() => parseContact(searchText.value))

const contactInitial = computed(() => {
  const value = parsedContact.value?.email || parsedContact.value?.phone || ''
  const firstAlpha = value.replace(/[^A-Za-z]/g, '').charAt(0)
  return (firstAlpha || value.charAt(0) || '').toUpperCase()
})

const addNew = (contact = '') => {
  emit('addNew', contact)
  searchText.value = ''
  results.value = []
  hasSearched.value = false
  isOpen.value = false
}

const addParsedContact = () => {
  const parsed = parsedContact.value
  if (!parsed) return
  addNew(parsed.email || parsed.phone || '')
}

type Option =
  | { kind: 'member', member: SearchMember }
  | { kind: 'contact' }
  | { kind: 'addNew' }

const options = computed<Option[]>(() => {
  if (!hasSearched.value || isLoading.value) return []
  const list: Option[] = []
  if (results.value.length > 0) {
    for (const member of results.value) {
      if (!isDisabled(member)) list.push({ kind: 'member', member })
    }
  } else if (parsedContact.value) {
    list.push({ kind: 'contact' })
  }
  if (!props.hideAddNew) list.push({ kind: 'addNew' })
  return list
})

const activeIndex = ref(-1)
const listRef = ref<HTMLElement | null>(null)

watch(options, () => { activeIndex.value = -1 })

const isActive = (option: Option) => {
  const active = options.value[activeIndex.value]
  if (!active || active.kind !== option.kind) return false
  return option.kind !== 'member' || (active as typeof option).member.member_id === option.member.member_id
}

const isMemberActive = (member: SearchMember) => isActive({ kind: 'member', member })

const choose = (option: Option) => {
  if (option.kind === 'member') selectMember(option.member)
  else if (option.kind === 'contact') addParsedContact()
  else addNew()
}

const moveActive = async (delta: number) => {
  const count = options.value.length
  if (!count) return
  isOpen.value = true
  activeIndex.value = (activeIndex.value + delta + count) % count
  await nextTick()
  listRef.value?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveActive(1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(-1)
  } else if (event.key === 'Enter') {
    const option = options.value[activeIndex.value]
    if (!isOpen.value || !option) return
    event.preventDefault()
    choose(option)
  } else if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault()
    event.stopPropagation()
    isOpen.value = false
  }
}
</script>

<template>
  <Popover :open="isOpen && (hasSearched || isLoading)" @update:open="isOpen = $event">
    <PopoverTrigger as-child>
      <div class="relative">
        <Search class="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          v-model="searchText"
          :placeholder="placeholder || 'ex. Maria, john@acme.com, or +1234...'"
          class="pl-9"
          @focus="searchText.length >= 2 && (isOpen = true)"
          @keydown="onKeydown"
        />
      </div>
    </PopoverTrigger>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] p-0"
      align="start"
      :side-offset="4"
      @open-auto-focus.prevent
    >
      <div v-if="isLoading" class="p-3 text-center text-sm text-muted-foreground">
        Searching...
      </div>
      <div v-else-if="hasSearched" ref="listRef">
        <div v-if="results.length > 0" class="max-h-[200px] overflow-y-auto">
          <button
            v-for="member in results"
            :key="member.member_id"
            class="flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
            :class="isDisabled(member)
              ? 'opacity-50 cursor-not-allowed'
              : ['hover:bg-accent cursor-pointer', { 'bg-accent': isMemberActive(member) }]"
            :data-active="isMemberActive(member)"
            :disabled="isDisabled(member)"
            @click="selectMember(member)"
          >
            <MemberAvatar :avatar_url="member.avatar_url" :verified="member.verified" />
            <div class="flex flex-col min-w-0">
              <span class="truncate font-medium flex items-center gap-1">
                {{ member.display_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown' }}
                <VerifiedMarker v-if="member.verified" class="shrink-0" />
              </span>
              <span class="truncate text-xs text-muted-foreground" v-if="member.email">{{ member.email }}</span>
              <span class="truncate text-xs text-muted-foreground" v-if="member.phone">{{ member.phone }}</span>
              <span class="text-xs text-muted-foreground italic" v-if="member.is_project_member">Already a member of this project</span>
              <span class="text-xs text-muted-foreground italic" v-else-if="member.is_invited">Invite already exists</span>
            </div>
          </button>
        </div>
        <button
          v-else-if="parsedContact"
          class="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent cursor-pointer"
          :class="{ 'bg-accent': isActive({ kind: 'contact' }) }"
          :data-active="isActive({ kind: 'contact' })"
          @click="addParsedContact"
        >
          <Avatar class="w-8 h-8 bg-primary">
            <AvatarFallback class="text-white font-semibold">{{ contactInitial }}</AvatarFallback>
          </Avatar>
          <div class="flex flex-col min-w-0">
            <span class="flex items-center gap-1">
              <Badge class="px-2 py-0 text-xs">New member</Badge>
            </span>
            <span class="truncate text-xs text-muted-foreground">
              {{ parsedContact.email || parsedContact.phone }}
            </span>
          </div>
        </button>
        <p v-else class="px-3 py-2 text-sm text-muted-foreground">No members found</p>
        <button
          v-if="!hideAddNew"
          class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer m-1"
          :class="{ 'bg-accent': isActive({ kind: 'addNew' }) }"
          :data-active="isActive({ kind: 'addNew' })"
          @click="addNew()"
        >
          <UserPlus class="h-4 w-4" />
          <span>Add non-existing user</span>
        </button>
      </div>
    </PopoverContent>
  </Popover>
</template>
